import 'react-phone-number-input/style.css';

import { INLINES } from '@contentful/rich-text-types';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import * as R from 'ramda';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SubmitErrorHandler } from 'react-hook-form/dist/types/form';
import { useSelector } from 'react-redux';

import NormalButton from '@/components/button/normal';
import Captcha from '@/components/captcha';
import Collapsable from '@/components/lottery-form/collapsable';
import ErrorModalContent from '@/components/lottery-form/modals/error';
import SuccessModalContent from '@/components/lottery-form/modals/success';
import Modal from '@/components/modal';
import LoginButton from '@/components/navigation/components/login-button';
import RichTextRenderer, {
  renderInlineHyperlink,
} from '@/components/rich-text-renderer';
import { EMAIL_REGEX_PATTERN } from '@/lib/constants';
import getThemedButtonProps from '@/lib/get-themed-button-props';
import getVariablePlainText from '@/lib/get-variable-plain-text';
import useVariables from '@/lib/hooks/use-variables';
import {
  AdditionalData,
  LotteryFormData,
  ParticipantPersonalData,
  participate,
} from '@/lib/lottery2';
import {
  BIRTHDATE_PATTERN,
  BIRTHDATE_PLACEHOLDER,
  countryOptions,
  genderOptions,
} from '@/lib/participant-field-options';
import { capitalize, formatDate, notNull } from '@/lib/utils';
import { selectGlobalPageTheme } from '@/store/slices/global';
import { selectUserProfile } from '@/store/slices/user';
import {
  LotteryFormButton as LotteryFormButtonType,
  LotteryState,
} from '@/types/views/generic';

import { Checkbox, CheckboxGroup } from './checkbox';
import LotteryFile from './file';
import LotteryInput from './input';
import Phone from './phone';
import Select from './select';

const FORM_ID_QUERY_PARAM_KEY = 'openFormPotId';

export interface Field {
  name: string;
  required: string | boolean;
  pattern?: RegExp;
  type: string;
  label: string | JSX.Element;
  placeholder: string;
  options?: ReadonlyArray<{
    value: string;
    label: string;
  }>;
}

interface LotteryFormProps
  extends Omit<LotteryFormButtonType, 'componentType' | 'ctaText'> {
  onClose: () => void;
}

const LotteryForm: FC<LotteryFormProps> = ({
  lotteryTitle,
  lotteryPot,
  initialLotteryState = LotteryState.Form,
  onClose,
}) => {
  const router = useRouter();
  const [lotteryState, setLotteryState] =
    useState<LotteryFormButtonType['initialLotteryState']>(initialLotteryState);
  const variables = useVariables();
  const { publicRuntimeConfig } = getConfig();
  const userProfile = useSelector(selectUserProfile);
  const userCountry = countryOptions.find(
    (country) => country.value === userProfile?.energyAddress?.country,
  );
  const pageTheme = useSelector(selectGlobalPageTheme);
  const buttonProps = getThemedButtonProps(pageTheme);

  const collapsedFields = [
    'gender',
    'firstName',
    'lastName',
    'email',
    'country',
    'mobileNumber',
    'birthdate',
    'address',
    'zipCode',
    'city',
  ];

  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (userProfile) {
      return ![
        userProfile.gender,
        userProfile.firstName,
        userProfile.lastName,
        userProfile.email,
        userCountry,
        userProfile.mobileNumber,
        userProfile.birthdate,
        userProfile.energyAddress?.addressLine1,
        userProfile.energyAddress?.zipCode,
        userProfile.energyAddress?.city,
      ]
        .map((value) => Boolean(value))
        .some((value) => !value);
    }

    return false;
  });

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isValid, isSubmitting },
    setValue,
    watch,
    control,
  } = useForm<LotteryFormData>({
    mode: 'all',
  });

  useEffect(() => {
    if (userProfile) {
      if (userProfile.gender === 'female') {
        setValue('gender', 'f');
      } else if (userProfile.gender === 'none') {
        setValue('gender', 'd');
      } else if (userProfile.gender === 'male') {
        setValue('gender', 'm');
      }

      setValue('firstName', userProfile.firstName || '');
      setValue('lastName', userProfile.lastName || '');
      setValue('email', userProfile.email || '');

      if (userCountry?.value) {
        setValue('country', userCountry.value);
      }

      setValue('phone', userProfile.mobileNumber || '');
      setValue(
        'birthdate',
        userProfile.birthdate
          ? formatDate(userProfile.birthdate, 'dd.MM.yyyy')
          : '',
      );
      setValue(
        'address',
        `${userProfile.energyAddress?.addressLine1 || ''} ${
          userProfile.energyAddress?.addressLine2 || ''
        }`.trim() || '',
      );
      setValue('zipCode', userProfile.energyAddress?.zipCode || '');
      setValue('city', userProfile.energyAddress?.city || '');
    }
  }, [userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Used to reopen modal after logged in
  useEffect(() => {
    prepareRedirect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const genderLabel =
    getVariablePlainText(variables['lottery-form-foa-text']) || 'Anrede';
  const firstNameLabel =
    getVariablePlainText(variables['lottery-form-first-name-text']) ||
    'Vorname';
  const lastNameLabel =
    getVariablePlainText(variables['lottery-form-last-name-text']) ||
    'Nachname';
  const emailLabel =
    getVariablePlainText(variables['lottery-form-email-text']) ||
    'E-mail-Adresse';
  const countryLabel =
    getVariablePlainText(variables['lottery-form-country-text']) || 'Land';
  const phoneLabel =
    getVariablePlainText(variables['lottery-form-mobile-text']) ||
    'Handynummer';
  const birthDateLabel =
    getVariablePlainText(variables['lottery-form-birthday-text']) ||
    'Geburtsdatum';
  const addressLabel =
    getVariablePlainText(variables['lottery-form-street-text']) || 'Strasse';
  const zipCodeLabel =
    getVariablePlainText(variables['lottery-form-zip-code-text']) || 'PLZ';
  const cityLabel =
    getVariablePlainText(variables['lottery-form-locality-text']) || 'ORT';
  const termsLabel = (
    <RichTextRenderer
      body={variables['lottery-form-tac-text']}
      options={{
        renderNode: {
          [INLINES.HYPERLINK]: renderInlineHyperlink(),
        },
      }}
    />
  ) || (
    <span>
      Ich habe die{' '}
      <a
        className="text-red-light hover:text-red"
        href="https://energy.ch/rechtliches/datenschutzbestimmungen"
      >
        Datenschutzbestimmungen
      </a>{' '}
      gelesen und akzeptiere diese
    </span>
  );
  const marketingLabel =
    getVariablePlainText(variables['lottery-form-data-partner-text']) ||
    'Meine Daten dürfen für Marketingzwecke durch den Partner verwendet werden.';
  const submitLabel =
    getVariablePlainText(variables['lottery-form-cta-text']) ||
    'Jetzt Teilnehmen';
  const cancelText =
    getVariablePlainText(variables['lottery-form-cancel']) || 'Abbrechen';
  const emailErrorText = getVariablePlainText(variables['email-error-text']);
  const emailPlaceholderText = getVariablePlainText(
    variables['newsletter-signup-email-text'],
  );

  const fields: Field[] = [
    {
      name: 'gender',
      required: `${genderLabel} ist ein Pflichtfeld.`,
      type: 'select',
      label: genderLabel,
      placeholder: 'Auswählen',
      options: genderOptions,
    },
    {
      name: 'firstName',
      required: `${firstNameLabel} ist ein Pflichtfeld.`,
      type: 'text',
      label: firstNameLabel,
      placeholder: firstNameLabel,
    },
    {
      name: 'lastName',
      required: `${lastNameLabel} ist ein Pflichtfeld.`,
      type: 'text',
      label: lastNameLabel,
      placeholder: lastNameLabel,
    },
    {
      name: 'email',
      required: emailErrorText || `${emailLabel} ist ein Pflichtfeld.`,
      pattern: EMAIL_REGEX_PATTERN,
      type: 'email',
      label: emailLabel,
      placeholder: emailPlaceholderText || emailLabel,
    },
    {
      name: 'country',
      required: `${countryLabel} ist ein Pflichtfeld.`,
      type: 'select',
      label: countryLabel,
      placeholder: countryLabel,
      options: countryOptions,
    },
    {
      name: 'phone',
      required:
        'Die Handynummer wird verwendet, um dich zu kontaktieren. Stelle sicher, dass die Nummer korrekt ist.',
      type: 'phone',
      label: phoneLabel,
      placeholder: 'z.B. +41791002030',
    },
    {
      name: 'birthdate',
      required: `${birthDateLabel} ist ein Pflichtfeld.`,
      pattern: BIRTHDATE_PATTERN,
      type: 'text',
      label: birthDateLabel,
      placeholder: BIRTHDATE_PLACEHOLDER,
    },
    {
      name: 'address',
      required: `${addressLabel} ist ein Pflichtfeld.`,
      type: 'text',
      label: addressLabel,
      placeholder: addressLabel,
    },
    {
      name: 'zipCode',
      required: `${zipCodeLabel} ist ein Pflichtfeld.`,
      pattern: /^\d{1,5}$/,
      type: 'text',
      label: zipCodeLabel,
      placeholder: 'z.B 0000',
    },
    {
      name: 'city',
      required: `${cityLabel} ist ein Pflichtfeld.`,
      type: 'text',
      label: cityLabel,
      placeholder: cityLabel,
    },
  ];

  const customFields: Field[] = lotteryPot.customFields
    .map((field) => {
      if (field.type === 'textarea') {
        const displayName = field.displayName || capitalize(field.name);

        return {
          name: `cf_${field.id}`,
          required: `${displayName} ist ein Pflichtfeld.`,
          type: 'text',
          label: displayName,
          placeholder: displayName,
        };
      }

      if (field.type === 'select') {
        const displayName = field.displayName || capitalize(field.name);

        return {
          name: `cf_${field.id}`,
          required: `${displayName} ist ein Pflichtfeld.`,
          type: 'select',
          label: displayName,
          placeholder: displayName,
          options: field.settings.options?.map((option) => ({
            value: option.value,
            label: option.title,
          })),
        };
      }

      if (field.type === 'checkbox') {
        const displayName = field.displayName || capitalize(field.name);

        return {
          name: `cf_${field.id}`,
          required: false,
          type: 'checkbox',
          label: displayName,
          placeholder: displayName,
          options: field.settings.options?.map((option) => ({
            value: option.value,
            label: option.title,
          })),
        };
      }

      if (field.type === 'file') {
        const displayName = field.displayName || capitalize(field.name);

        return {
          name: `cf_${field.id}`,
          required: `${displayName} ist ein Pflichtfeld.`,
          type: 'file',
          label: displayName,
          placeholder: displayName,
        };
      }

      return null;
    })
    .filter(notNull);

  const endCheckboxes: Field[] = [
    {
      name: 'terms',
      required: 'Datenschutzbestimmungen ist ein Pflichtfeld.',
      type: 'basic_checkbox',
      label: termsLabel,
      placeholder: '',
    },
    {
      name: 'emailMarketing',
      required: false,
      type: 'basic_checkbox',
      label: marketingLabel,
      placeholder: '',
    },
  ];

  /**
   * Input: {
   *      cf_467: 'test1',
   *     'cf_469 checkbox_value: Test 1': false,
   *     'cf_469 checkbox_value: Test 2': true,
   * }
   *
   * Output: {
   *   cf_469: ['Test 2'],
   * }
   */
  const mergeCheckboxCustomFields = (
    data: ParticipantPersonalData &
      AdditionalData &
      Record<string, string | boolean>,
  ) => {
    const checkboxAnswers: Record<string, string[]> = {};

    R.keys(data)
      .filter((keyName) => keyName.includes('checkbox_value'))
      .forEach((keyName) => {
        const fieldName = /(.*) checkbox_value.*/.exec(keyName)?.[1] || null;

        if (fieldName) {
          if (!checkboxAnswers[fieldName]) {
            checkboxAnswers[fieldName] = [];
          }
          const value = /.* checkbox_value: (.*)/.exec(keyName)?.[1] || null;

          if (value && data[keyName] === true) {
            checkboxAnswers[fieldName].push(value);
          }
        }
      });

    return checkboxAnswers;
  };

  const onSubmit: SubmitHandler<
    ParticipantPersonalData & AdditionalData & Record<string, string>
  > = async (data) => {
    const checkboxFields = R.keys(data).filter((keyName) =>
      keyName.includes('checkbox_value'),
    );
    const checkboxesJoint = R.pipe(
      // @ts-ignore Fix typing
      mergeCheckboxCustomFields,
      R.map((arr) => arr.join(', ') || '-'),
      // @ts-ignore Fix typing
    )(data);
    const mappedData = R.pipe(
      R.omit(checkboxFields),
      R.mergeRight(checkboxesJoint),
    )(data);

    if (isValid) {
      const {
        error,
        data: resData,
        response,
        // @ts-ignore Fix typing
      } = await participate(mappedData, lotteryPot);

      if (response?.ok) {
        setLotteryState(LotteryState.Success);
      } else {
        console.error(error);

        if (
          typeof resData?.message === 'string' &&
          resData.message.includes('alreadyTaken')
        ) {
          setLotteryState(LotteryState.ErrorAlreadyParticipated);
        } else {
          setLotteryState(LotteryState.Error);
        }
      }
    }
  };

  const onError: SubmitErrorHandler<ParticipantPersonalData> = (
    submitErrors,
  ) => {
    const errorKeys = Object.keys(submitErrors);
    const includesCollapsedField = errorKeys.some((errorKey) =>
      collapsedFields.includes(errorKey),
    );

    if (includesCollapsedField) {
      setIsCollapsed(false);
    }
  };

  const onCloseModal = () => {
    reset();
    onClose();
    setLotteryState(initialLotteryState);
    clearRedirect();
  };

  const renderInput = (field: Field) => {
    if (['text', 'email'].includes(field.type)) {
      return <LotteryInput register={register} field={field} errors={errors} />;
    }

    if (field.type === 'checkbox') {
      if (field.options && field.options.length) {
        return (
          <CheckboxGroup
            register={register}
            field={{
              options: field.options,
              ...field,
            }}
          />
        );
      }

      return <Checkbox register={register} field={field} showAnswerAsText />;
    }

    if (field.type === 'phone') {
      return <Phone control={control} errors={errors} field={field} />;
    }

    if (field.type === 'select') {
      return <Select register={register} field={field} />;
    }

    if (field.type === 'file') {
      return (
        <LotteryFile
          register={register}
          watch={watch}
          field={field}
          errors={errors}
          onUploaded={(filename) => {
            if (filename) setValue(field.name, filename);
          }}
        />
      );
    }

    if (field.type === 'basic_checkbox') {
      return <Checkbox register={register} field={field} />;
    }

    return null;
  };

  const prepareRedirect = () => {
    if (!window) return;

    const url = new URL(window?.location.href);
    const params = new URLSearchParams(url.search);

    if (!params.has(FORM_ID_QUERY_PARAM_KEY)) {
      params.append(FORM_ID_QUERY_PARAM_KEY, lotteryPot.potId?.toString());

      const urlWithoutSearch = `${window.location.origin}${window.location.pathname}`;
      const toAppend = params.toString();

      if (toAppend) {
        router.push(
          `${urlWithoutSearch}?${toAppend}`,
          `${urlWithoutSearch}?${toAppend}`,
          { shallow: true },
        );
      }
    }
  };

  const clearRedirect = () => {
    if (!window) return;

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const urlWithoutSearch = `${window.location.origin}${window.location.pathname}`;

    params.delete(FORM_ID_QUERY_PARAM_KEY);

    const toAppend = params.toString();

    if (toAppend) {
      router.push(
        `${urlWithoutSearch}?${toAppend}`,
        `${urlWithoutSearch}?${toAppend}`,
        { shallow: true },
      );
    } else {
      router.push(urlWithoutSearch, urlWithoutSearch, {
        shallow: true,
      });
    }
  };

  const customFieldsElements = customFields.map((field) => (
    <div key={field.name} className="mb-6">
      {renderInput(field)}
    </div>
  ));

  return (
    <Modal onClose={onCloseModal}>
      <div className="content-box md:content-box-none md:w-3/4 md:mx-auto py-12">
        <div className="flex flex-col items-center">
          {lotteryState === LotteryState.Success && <SuccessModalContent />}

          {lotteryState === LotteryState.Error && <ErrorModalContent />}

          {lotteryState === LotteryState.ErrorAlreadyParticipated && (
            <ErrorModalContent
              titleSlug="form-already-participating-title"
              textSlug="form-already-participating-text"
            />
          )}

          {lotteryState === LotteryState.Form && (
            <>
              <span className="text-pink text-h4 font-semibold mb-4 tesn:text-esn-primary teair:text-air-mediumBlue">
                {lotteryTitle}
              </span>

              <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="flex flex-col w-full"
              >
                {lotteryPot.customFieldPositionTop
                  ? customFieldsElements
                  : null}

                {userProfile ? (
                  <Collapsable
                    isCollapsed={isCollapsed}
                    onClick={() => setIsCollapsed((prevState) => !prevState)}
                    title={
                      getVariablePlainText(
                        variables[
                          isCollapsed
                            ? 'form-autofill-expand-text'
                            : 'form-autofill-close-text'
                        ],
                      ) ?? ''
                    }
                  >
                    {fields.map((field) => (
                      <div key={field.name} className="mb-6">
                        {renderInput(field)}
                      </div>
                    ))}
                  </Collapsable>
                ) : (
                  <div>
                    <div className="flex flex-row justify-start items-center mb-6">
                      <LoginButton />

                      <span className="ml-6">
                        {getVariablePlainText(
                          variables['lottery-form-not-logged-in'],
                        ) ?? 'Save time filling this form up by logging in'}
                      </span>
                    </div>

                    <div>
                      {fields.map((field) => (
                        <div key={field.name} className="mb-6">
                          {renderInput(field)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!lotteryPot.customFieldPositionTop
                  ? customFieldsElements
                  : null}

                <div className="my-6">
                  {endCheckboxes.map((field) => (
                    <div key={field.name} className="mb-6">
                      {renderInput(field)}
                    </div>
                  ))}
                </div>

                <input
                  {...register('captcha', {
                    required: true,
                  })}
                  type="hidden"
                  id="captcha"
                  name="captcha"
                />

                <Captcha
                  siteKey={publicRuntimeConfig.lottery2recaptchaSiteKey}
                  onChange={(value) => setValue('captcha', value || '')}
                />

                {errors.captcha ? (
                  <div className="text-red-medium">
                    {getVariablePlainText(variables['captcha-error']) ||
                      'ReCaptcha error. Try reloading page.'}
                  </div>
                ) : null}

                <div className="flex items-center flex-col md:flex-row">
                  {isSubmitting ? (
                    <NormalButton
                      type="button"
                      text={
                        getVariablePlainText(variables.loading) || 'Loading'
                      }
                      disabled
                    />
                  ) : (
                    <>
                      <NormalButton
                        {...buttonProps}
                        type="submit"
                        text={submitLabel}
                      />
                    </>
                  )}

                  <button
                    type="button"
                    className="text-body2 text-gray-2 font-semibold underline mt-6 md:mt-0 md:ml-6"
                    onClick={onCloseModal}
                  >
                    {cancelText}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LotteryForm;
